/**
 * StaffSnsSignupPage — A-4-6. SNS 가입 사업자 확인 (직원)
 * 진입: SnsRoleSelectPage → role=staff → /signup/sns/staff (route state)
 *
 * 전화번호 로직: MasterSnsSignupPage와 동일
 *   blur(10자리+) → GET /member/users/exists?phone=
 *     exists=false  → 녹색 / 폼 제출 → POST /member/join/sns (master:false)
 *     exists=true   → OTP UI → sns-verify → LINKED → login-with-token
 *
 * 사업자 검색: GET /member/biz/info?compNo= → 결과 카드 표시
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@indipia/shared';
import { checkUserExists, requestPhoneVerify, verifyPhoneCode, snsVerify } from '../api/member-auth';
import { joinSns, searchBiz, type BizSearchItem } from '../api/member-join';
import { loginWithSnsToken } from '../api/social-auth';
import { applyLoginTokens } from '../lib/post-login';
import { TermsConsentSection, INITIAL_TERMS_CONSENT, type TermsConsentValue } from '../components/TermsConsentSection';
import { PhoneOtpVerify, PhoneOtpSendButton } from '../components/PhoneOtpVerify';

interface SnsRouteState {
  provider:     string;
  socialId:     string;
  email:        string | null;
  socialName:   string | null;
  mobile:       string | null;
  snsLinkToken: string;
}

// ── 공용 스타일 ──────────────────────────────────────────────────────────

function inpStyle(overrides?: React.CSSProperties): React.CSSProperties {
  return {
    width: '100%', padding: '13px 16px',
    background: 'var(--shell-bg)', border: '1px solid var(--shell-border)',
    borderRadius: 'var(--radius-md)', color: 'var(--shell-on-surface)',
    outline: 'none', fontFamily: 'inherit',
    transition: 'border-color .15s, box-shadow .15s',
    ...overrides,
  };
}
const INPUT_CLASS = 'text-body-sm';

const labelStyle: React.CSSProperties = { marginBottom: 6, display: 'block' };
const LABEL_CLASS = 'text-caption font-semibold text-text';

/** 인디고 브랜드 면(shell.point) 위 흰 계열 — hex·rgba 없이 토큰으로 표현 */
const onPoint = (pct: number) => `color-mix(in srgb, var(--gt-on-primary) ${pct}%, transparent)`;

/** 표준 .input 포커스/블러 — border + ring (D-09 §15.2) */
const focusInp = (el: HTMLInputElement) => {
  el.style.borderColor = 'var(--gt-primary)';
  el.style.boxShadow = '0 0 0 3px var(--gt-subtle)';
};
const blurInp = (el: HTMLInputElement, errored = false) => {
  el.style.borderColor = errored ? 'var(--color-error)' : 'var(--shell-border)';
  el.style.boxShadow = 'none';
};


// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────

export function StaffSnsSignupPage() {
  const navigate    = useNavigate();
  const location    = useLocation();
  const queryClient = useQueryClient();
  const setAuth     = useAuthStore((s) => s.setAuth);
  const sns         = (location.state ?? {}) as Partial<SnsRouteState>;

  // ── 전화번호 포맷 ──────────────────────────────────────────────────────
  const formatPhone = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length < 4) return d;
    if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  };

  // ── 폼 상태 ────────────────────────────────────────────────────────────
  const [keyword,     setKeyword]     = useState('');
  const [bizResults,  setBizResults]  = useState<BizSearchItem[] | null>(null);
  const [bizSelected, setBizSelected] = useState<BizSearchItem | null>(null);
  const [bizError,    setBizError]    = useState<string | null>(null);
  const [bizLoading,  setBizLoading]  = useState(false);
  const [mobile,     setMobile]     = useState(formatPhone(sns.mobile ?? ''));
  const [email,      setEmail]      = useState(sns.email ?? '');

  // ── SNS가 전화번호를 제공했는지 여부 — 제공된 번호는 이름처럼 수정 불가(신뢰), 미제공 시 본인이 입력 후 인증 ──
  const hasSnsMobile = Boolean(sns.mobile);

  // ── 전화번호 중복 체크 상태 ─────────────────────────────────────────────
  const [phoneCheckDone,    setPhoneCheckDone]    = useState(false);
  const [phoneHasDuplicate, setPhoneHasDuplicate] = useState(false);
  const [phoneCheckLoading, setPhoneCheckLoading] = useState(false);
  const lastCheckedPhone = useRef('');

  // ── OTP 상태 ───────────────────────────────────────────────────────────
  const [otpSent,     setOtpSent]     = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading,  setOtpLoading]  = useState(false);
  const [otpError,    setOtpError]    = useState<string | null>(null);

  // ── 제출 상태 ──────────────────────────────────────────────────────────
  const [isLoading,   setIsLoading]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [terms,       setTerms]       = useState<TermsConsentValue>(INITIAL_TERMS_CONSENT);

  // ── 검증 상태 (blur/제출 시 표시) ────────────────────────────────────────
  const [mobileTouched, setMobileTouched] = useState(false);
  const [otpTouched,    setOtpTouched]    = useState(false);
  const [emailTouched,  setEmailTouched]  = useState(false);
  const [termsTouched,  setTermsTouched]  = useState(false);
  const mobileDigitsForErr  = mobile.replace(/\D/g, '');
  const mobileIsMobileFormat = mobileDigitsForErr.startsWith('010') || mobileDigitsForErr.startsWith('999'); // 999=테스트 목업 번호
  const mobileErr = mobileTouched && mobileDigitsForErr.length >= 10 && !mobileIsMobileFormat ? '010으로 시작하는 휴대폰 번호를 입력해 주세요.'
    : mobileTouched && mobileDigitsForErr.length < 10 ? '전화번호를 정확히 입력해 주세요.'
    : null;
  const otpErr    = otpTouched && phoneCheckDone && (phoneHasDuplicate || !hasSnsMobile) && !otpVerified ? '전화번호 인증을 완료해 주세요.' : null;
  const emailErr  = emailTouched  && !email.includes('@')          ? '이메일 형식이 올바르지 않아요.'   : null;
  const termsErr  = termsTouched  && !terms.terms ? '필수 약관에 동의해 주세요.' : null;

  const handleMobileChange = (v: string) => {
    const formatted = formatPhone(v);
    setMobile(formatted);
    const digits = formatted.replace(/\D/g, '');
    if (digits !== lastCheckedPhone.current.replace(/\D/g, '')) {
      setPhoneCheckDone(false);
      setPhoneHasDuplicate(false);
      setOtpSent(false);
      setOtpVerified(false);
      setOtpError(null);
    }
  };

  // ── 전화번호 blur → 중복 체크 ──────────────────────────────────────────
  const handlePhoneBlur = async () => {
    setMobileTouched(true);
    const digits = mobile.replace(/\D/g, '');
    if (digits.length < 10) return;
    if (digits === lastCheckedPhone.current.replace(/\D/g, '') && phoneCheckDone) return;

    setPhoneCheckLoading(true);
    try {
      const result = await checkUserExists(mobile);
      lastCheckedPhone.current = mobile;
      setPhoneHasDuplicate(result.exists);
      setPhoneCheckDone(true);
      setOtpSent(false);
      setOtpVerified(false);
      setOtpError(null);
    } catch {
      // 체크 실패 시 조용히 무시
    } finally {
      setPhoneCheckLoading(false);
    }
  };

  // ── SNS가 번호를 제공한 경우 — 신뢰하고 잠그되, 중복 계정 연동 여부는 확인해야 함 ──
  useEffect(() => {
    if (!hasSnsMobile) return;
    void handlePhoneBlur();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── OTP 발송 ───────────────────────────────────────────────────────────
  const handleOtpRequest = async () => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      await requestPhoneVerify(mobile);
      setOtpSent(true);
    } catch {
      setOtpError('인증번호 발송에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ── OTP 확인 → sns-verify 즉시 호출 ──────────────────────────────────
  const handleOtpVerify = async (code: string) => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      const otpResult = await verifyPhoneCode(mobile, code);
      if (!otpResult.verified || !otpResult.verifyToken) {
        setOtpError('인증번호가 일치하지 않습니다.');
        return;
      }
      // OTP 성공 → SNS 연동 여부 즉시 확인
      const snsResult = await snsVerify({
        snsType:      (sns.provider ?? '').toUpperCase(),
        snsSubjectId: sns.socialId  ?? '',
        mobile,
        verifyToken:  otpResult.verifyToken,
        email,
        name: sns.socialName ?? '',
      });
      if (snsResult.status === 'LINKED') {
        // 이미 연동된 SNS → 버튼 없이 바로 로그인
        const tokens = await loginWithSnsToken(sns.snsLinkToken ?? '');
        applyLoginTokens(tokens, setAuth, queryClient);
        void navigate('/app', { replace: true });
        return;
      } else if (snsResult.status === 'ALREADY_LINKED') {
        setOtpError('이미 다른 계정에 연동된 SNS입니다.');
        return;
      }
      setOtpVerified(true);
    } catch {
      setOtpError('인증번호 확인에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ── 상호명 검색 ────────────────────────────────────────────────────────
  const handleBizSearch = async () => {
    if (keyword.trim().length < 2) {
      setBizError('2자 이상 입력해 주세요.');
      return;
    }
    setBizLoading(true);
    setBizError(null);
    setBizResults(null);
    setBizSelected(null);
    try {
      const results = await searchBiz(keyword.trim());
      if (results.length === 0) {
        setBizError('검색 결과가 없어요. 사장님께 인솔 가입을 먼저 요청해 주세요.');
      } else {
        setBizResults(results);
      }
    } catch {
      setBizError('사업장 검색에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setBizLoading(false);
    }
  };

  // 제출 클릭 시 검증 — 문제 있는 필드에 에러 표시, 통과해야만 제출 진행
  const validateForm = (): boolean => {
    setMobileTouched(true);
    setOtpTouched(true);
    setTermsTouched(true);
    let ok = true;

    if (mobileDigitsForErr.length < 10) ok = false;
    else if (phoneCheckLoading)         ok = false;
    else if (!phoneCheckDone)           { void handlePhoneBlur(); ok = false; }

    if (!terms.terms) ok = false;

    // 이미 가입된 번호(SNS 전용 계정 연동) 흐름 — OTP 인증만 통과하면 됨
    if (phoneHasDuplicate) {
      if (!otpVerified) ok = false;
      return ok;
    }

    // 중복은 아니지만 SNS가 번호를 제공하지 않아 직접 입력한 경우 — 본인 확인 OTP 필수
    if (!hasSnsMobile && !otpVerified) ok = false;

    setEmailTouched(true);
    if (!bizSelected)          { setBizError('소속 사업장을 검색해 선택해 주세요.'); ok = false; }
    if (!email.includes('@'))  ok = false;
    return ok;
  };

  // ── 폼 제출 (joinSns만 담당 — LINKED 케이스는 handleOtpVerify에서 처리) ──
  const handleSubmit = async () => {
    setIsLoading(true);
    setSubmitError(null);
    try {
      await joinSns({
        snsType:            (sns.provider ?? '').toUpperCase(),
        snsSubjectId:       sns.socialId  ?? '',
        memberName:         sns.socialName ?? '',
        email,
        mobile,
        master:             false,
        compNo:              bizSelected?.compNo ?? '',
        marketingConsent:   terms.marketing,
        marketingChannels:  terms.channels,
        nightMarketingConsent: terms.nightAd,
      });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      setSubmitError(err?.response?.data?.message ?? '가입 처리 중 오류가 발생했습니다.');
      setIsLoading(false);
      return;
    }

    // ── 가입 성공 → 자동로그인 (실패해도 "가입 성공"은 유지, 로그인 화면으로 폴백) ──
    try {
      const tokens = await loginWithSnsToken(sns.snsLinkToken ?? '');
      applyLoginTokens(tokens, setAuth, queryClient);
      void navigate('/app', { replace: true });
    } catch {
      void navigate('/login', { replace: true, state: { signupDone: true } });
    } finally {
      setIsLoading(false);
    }
  };

  const providerLabel: Record<string, string> = { kakao: '카카오', naver: '네이버', google: '구글' };
  const pLabel = providerLabel[sns.provider ?? ''] ?? 'SNS';

  // ── 렌더 ──────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen flex-col overflow-hidden bg-surface md:flex-row">

      {/* 좌측 브랜딩 */}
      <div className="relative flex w-full shrink-0 flex-col overflow-hidden bg-primary-700 px-7 pb-9 pt-10 md:w-[36%] md:min-h-screen md:px-11 md:pb-12 md:pt-[52px]">
        <div className="flex items-center gap-2">
          <span className="text-body-sm font-bold uppercase tracking-widest text-white">INDIPIA</span>
          <span className="bg-white/60" style={{ width: 5, height: 5, borderRadius: '50%', flexShrink: 0, display: 'inline-block' }} />
          <span className="text-caption font-semibold uppercase tracking-widest text-[var(--alpha-white-800)]">Pro</span>
        </div>

        {/* 직원 배지 */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'var(--alpha-white-200)', border: `1px solid ${onPoint(35)}`,
          borderRadius: 20, padding: '5px 12px', marginTop: 20, alignSelf: 'flex-start',
        }}>
          <span className="bg-white/60" style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, display: 'inline-block' }} />
          <span className="text-caption font-semibold text-white">직원 계정 가입</span>
        </div>

        {/* SNS 인증 배지 */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'var(--alpha-white-200)', border: `1px solid ${onPoint(35)}`,
          borderRadius: 20, padding: '6px 14px', marginTop: 10, marginBottom: 28, alignSelf: 'flex-start',
        }}>
          <span
            className="text-caption font-bold"
            style={{
              width: 22, height: 22, borderRadius: '50%', background: 'var(--brand-kakao-container)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--brand-kakao-label)', flexShrink: 0,
            }}
          >
            {(sns.provider ?? 'K')[0].toUpperCase()}
          </span>
          <span className="text-caption font-semibold text-white">
            {pLabel} 인증 완료
          </span>
        </div>

        <div className="flex flex-1 flex-col justify-center pb-8">
          <p className="text-caption font-semibold uppercase tracking-widest text-[var(--alpha-white-800)]" style={{ marginBottom: 18 }}>SNS Signup</p>
          <h2 className="text-display-sm font-bold text-white" style={{ wordBreak: 'keep-all' }}>
            소속 사업장을<br />확인해주세요.
          </h2>
          <div className="bg-white/60" style={{ width: 40, height: 2, margin: '22px 0', borderRadius: 2 }} />
          <p className="text-body-sm text-[var(--alpha-white-800)]">
            사업장명 검색 후<br />가입 신청이 완료됩니다.
          </p>
        </div>
        <p className="text-caption text-[var(--alpha-white-800)]">v0.5 · 2026</p>
      </div>

      {/* 우측 폼 */}
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto bg-surface-raised px-7 py-9 md:px-[64px] md:py-[52px]">
        <div style={{ width: '100%', maxWidth: 480 }}>

          {/* 직원 안내 배너 */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '14px 16px', background: 'color-mix(in srgb, var(--color-info) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-info) 20%, transparent)', borderRadius: 12, marginBottom: 28,
          }}>
            <span
              className="text-caption font-bold"
              style={{
                width: 20, height: 20, borderRadius: '50%', background: 'var(--color-info)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--gt-on-primary)', flexShrink: 0, marginTop: 1,
              }}
            >i</span>
            <p className="text-body-sm" style={{ color: 'var(--color-infoStrong)' }}>
              직원 계정으로 가입합니다. 소속 사업장 상호명을 검색해 주세요.
            </p>
          </div>

          <h1 className="text-heading-md font-bold text-text" style={{ marginBottom: 8 }}>사업자 확인</h1>
          <p className="text-body-sm text-text-secondary" style={{ marginBottom: 28 }}>
            SNS 인증이 완료되었어요. 소속 사업장 정보를 입력해 주세요.
          </p>

          {/* 이름 — SNS 자동입력 (readonly) */}
          <div style={{ marginBottom: 14 }}>
            <label className={LABEL_CLASS} style={labelStyle}>이름</label>
            <input type="text" readOnly aria-label="이름" value={sns.socialName ?? ''}
              className={INPUT_CLASS}
              style={inpStyle({ color: 'var(--shell-on-surface-2)', cursor: 'default', border: '1.5px solid var(--color-success)' })} />
          </div>

          {/* 소속 사업장 상호명 검색 */}
          <div style={{ marginBottom: 6 }}>
            <label className={LABEL_CLASS} style={labelStyle}>소속 사업장 검색 <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="상호명 입력 (2자 이상)"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setBizResults(null);
                  setBizSelected(null);
                  setBizError(null);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') void handleBizSearch(); }}
                className={INPUT_CLASS}
                style={inpStyle({
                  flex: 1,
                  borderColor: bizSelected ? 'var(--color-success)' : bizError ? 'var(--color-error)' : 'var(--shell-border)',
                })}
                onFocus={(e) => { if (!bizSelected) focusInp(e.target); }}
                onBlur={(e)  => { if (!bizSelected) blurInp(e.target); }}
              />
              <button
                type="button"
                onClick={() => void handleBizSearch()}
                disabled={bizLoading}
                className="text-body-sm font-bold"
                style={{
                  flexShrink: 0, padding: '0 18px', background: 'var(--gt-primary)', border: 'none',
                  borderRadius: 12, color: 'var(--gt-on-primary)',
                  cursor: bizLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
                  fontFamily: 'inherit', opacity: bizLoading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => { if (!bizLoading) e.currentTarget.style.filter = 'brightness(.92)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
              >
                {bizLoading ? '검색 중…' : '검색'}
              </button>
            </div>
          </div>

          {/* 검색 에러 */}
          {bizError && (
            <p className="text-caption" style={{ color: 'var(--color-error)', marginBottom: 10 }}>{bizError}</p>
          )}

          {/* 검색 결과 목록 */}
          {bizResults && !bizSelected && (
            <div style={{
              border: '1.5px solid var(--shell-border)', borderRadius: 12,
              overflow: 'hidden', marginBottom: 14, background: 'var(--shell-surface)',
            }}>
              {bizResults.map((item, i) => (
                <button
                  key={item.compId}
                  type="button"
                  onClick={() => { setBizSelected(item); setBizResults(null); }}
                  style={{
                    width: '100%', padding: '12px 16px', textAlign: 'left',
                    background: 'none', border: 'none', cursor: 'pointer',
                    borderTop: i > 0 ? '1px solid var(--shell-border)' : 'none',
                    fontFamily: 'inherit', display: 'block',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--shell-bg)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  <div className="text-body-sm font-semibold text-text" style={{ marginBottom: 2 }}>{item.compName}</div>
                  <div className="text-caption text-text-secondary">대표 {item.compRepName} · {item.compNo}</div>
                </button>
              ))}
            </div>
          )}

          {/* 선택된 사업장 카드 */}
          {bizSelected && (
            <div style={{
              padding: 16, background: 'var(--shell-bg)',
              border: '1.5px solid var(--color-success)', borderRadius: 12, marginBottom: 14,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span className="text-caption font-semibold" style={{ color: 'var(--color-success)' }}>사업장 선택 완료</span>
                </div>
                <button
                  type="button"
                  onClick={() => { setBizSelected(null); setBizResults(null); }}
                  className="text-caption text-text-secondary"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}
                >
                  변경
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="text-caption text-text-secondary" style={{ width: 60, flexShrink: 0 }}>상호명</span>
                  <span className="text-body-sm font-semibold text-text">{bizSelected.compName}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="text-caption text-text-secondary" style={{ width: 60, flexShrink: 0 }}>대표자명</span>
                  <span className="text-body-sm font-semibold text-text">{bizSelected.compRepName}</span>
                </div>
              </div>
            </div>
          )}

          <div style={{ height: 1, background: 'var(--shell-border)', margin: '4px 0 18px' }} />

          {/* 연락처 — SNS가 제공한 번호는 이름처럼 잠금(신뢰), 미제공 시 직접 입력 후 OTP 인증 */}
          <div style={{ marginBottom: 14 }}>
            <label className={LABEL_CLASS} style={labelStyle}>연락처 <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="tel" aria-label="연락처" placeholder="010-0000-0000"
                value={mobile}
                readOnly={hasSnsMobile}
                onChange={(e) => { if (!hasSnsMobile) handleMobileChange(e.target.value); }}
                onBlur={() => { if (!hasSnsMobile) void handlePhoneBlur(); }}
                className={INPUT_CLASS}
                style={inpStyle({
                  flex: 1,
                  cursor: hasSnsMobile ? 'default' : undefined,
                  color: hasSnsMobile ? 'var(--shell-on-surface-2)' : undefined,
                  borderColor: mobileErr
                    ? 'var(--color-error)'
                    : phoneCheckDone
                      ? phoneHasDuplicate
                        ? otpVerified ? 'var(--color-success)' : 'var(--color-warning)'
                        : 'var(--color-success)'
                      : 'var(--shell-border)',
                })}
                onFocus={(e) => {
                  if (!hasSnsMobile && !phoneCheckDone && !mobileErr) focusInp(e.target);
                }}
              />
              {phoneCheckDone && !phoneCheckLoading && mobileIsMobileFormat && (phoneHasDuplicate || !hasSnsMobile) && !otpSent && !otpVerified && (
                <PhoneOtpSendButton
                  onClick={() => void handleOtpRequest()}
                  loading={otpLoading}
                  label={phoneHasDuplicate ? '인증번호 받기' : '인증번호 발송'}
                />
              )}
            </div>
            {mobileErr && <p className="text-caption" style={{ marginTop: 5, color: 'var(--color-error)' }}>{mobileErr}</p>}
            {!mobileErr && phoneCheckLoading && <p className="text-caption text-text-secondary" style={{ marginTop: 5 }}>확인 중…</p>}
            {phoneCheckDone && !phoneHasDuplicate && !phoneCheckLoading && hasSnsMobile && (
              <p className="text-caption" style={{ marginTop: 5, color: 'var(--color-success)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                SNS로 인증된 번호예요
              </p>
            )}
            {phoneCheckDone && !phoneHasDuplicate && !phoneCheckLoading && !hasSnsMobile && (
              <PhoneOtpVerify
                otpSent={otpSent}
                otpVerified={otpVerified}
                onVerify={handleOtpVerify}
                loading={otpLoading}
                error={otpError}
                variant="verify"
              />
            )}
            {phoneCheckDone && phoneHasDuplicate && !phoneCheckLoading && (
              <PhoneOtpVerify
                otpSent={otpSent}
                otpVerified={otpVerified}
                onVerify={handleOtpVerify}
                loading={otpLoading}
                error={otpError}
                variant="link"
              />
            )}
            {otpErr && <p className="text-caption" style={{ marginTop: 5, color: 'var(--color-error)' }}>{otpErr}</p>}
          </div>

          {/* 이메일 — SNS 자동입력 (수정 가능) */}
          <div style={{ marginBottom: 20 }}>
            <label className={LABEL_CLASS} style={labelStyle}>이메일 <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input
              type="email" placeholder="이메일을 입력하세요"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={INPUT_CLASS}
              style={inpStyle({
                borderColor: emailErr ? 'var(--color-error)' : email && email.includes('@') ? 'var(--color-success)' : 'var(--shell-border)',
              })}
              onFocus={(e) => { if (!email.includes('@') && !emailErr) focusInp(e.target); }}
              onBlur={(e)  => {
                if (!email.includes('@')) blurInp(e.target);
                setEmailTouched(true);
              }}
            />
            {emailErr && <p className="text-caption" style={{ marginTop: 5, color: 'var(--color-error)' }}>{emailErr}</p>}
          </div>

          {/* 이용약관 */}
          <TermsConsentSection value={terms} onChange={setTerms} />
          {termsErr && <p className="text-caption" style={{ marginTop: -14, marginBottom: 16, color: 'var(--color-error)' }}>{termsErr}</p>}

          {/* 제출 에러 */}
          {submitError && (
            <div style={{ background: 'color-mix(in srgb, var(--color-error) 8%, transparent)', borderRadius: 12, padding: '10px 14px', marginBottom: 12 }}>
              <p className="text-caption font-medium" style={{ color: 'var(--color-error)' }}>{submitError}</p>
            </div>
          )}

          {/* 제출 버튼 */}
          <button
            type="button"
            onClick={() => { if (validateForm()) void handleSubmit(); }}
            disabled={isLoading}
            className="text-heading-sm font-bold"
            style={{
              width: '100%', padding: 15,
              background: !isLoading ? 'var(--gt-primary)' : 'var(--shell-border)',
              border: 'none', borderRadius: 14,
              color: !isLoading ? 'var(--gt-on-primary)' : 'var(--gray-400)',
              cursor: !isLoading ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              transition: 'filter .15s, transform .1s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
            onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.filter = 'brightness(.92)'; }}
            onMouseLeave={(e) => { if (!isLoading) e.currentTarget.style.filter = 'none'; }}
            onMouseDown={(e) => { if (!isLoading) e.currentTarget.style.transform = 'scale(.98)'; }}
            onMouseUp={(e)   => { e.currentTarget.style.transform = ''; }}
          >
            {isLoading && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {phoneHasDuplicate && otpVerified ? 'SNS 계정 연동하기' : '가입 신청하기 →'}
          </button>

        </div>
      </div>
    </div>
  );
}
